import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Archive } from 'lucide-react';
import { Card, CardGridSkeleton, EmptyState } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { RestoreToHostDialog } from '@/components/backups/RestoreToHostDialog';
import { SiteCopies } from '@/components/backups/SiteCopies';
import { useRoles } from '@/hooks/useAuth';
import { useBackupSites, useBackupsOverview } from '@/hooks/useBackups';
import { can } from '@/utils/permissions';
import type { BackupListItem } from '@/types/api';

/** The copies of a server's sites (ADR-0058), slot by slot as on the Backups page (ADR-0056). */
export function ServerCopies({ nodeId }: { readonly nodeId: string }): ReactNode {
  const roles = useRoles();
  const overview = useBackupsOverview();
  const sites = useBackupSites({ serverId: nodeId, q: '', problems: false });
  const [restoring, setRestoring] = useState<BackupListItem | null>(null);

  if (sites.isPending || overview.isPending) return <CardGridSkeleton cards={2} />;
  if (sites.isError) return <ErrorState error={sites.error} />;
  if (overview.isError) return <ErrorState error={overview.error} />;

  return (
    <div className="stack">
      <p className="text-sm muted">
        До {overview.data.copiesPerSite} копий на сайт, новая заменяет самую старую. Все копии и настройки — на странице{' '}
        <Link to="/backups">Резервные копии</Link>.
      </p>
      {sites.data.sites.length === 0 ? (
        <Card>
          <EmptyState icon={Archive} message="Копий сайтов этого сервера пока нет" />
        </Card>
      ) : (
        sites.data.sites.map((site) => (
          <SiteCopies
            key={site.siteId}
            site={site}
            copiesPerSite={overview.data.copiesPerSite}
            serverName={null}
            mayDownload={can(roles, 'secrets.manage')}
            mayRestore={can(roles, 'backups.restore_production')}
            onRestore={setRestoring}
          />
        ))
      )}
      {restoring ? <RestoreToHostDialog backup={restoring} onClose={() => setRestoring(null)} /> : null}
    </div>
  );
}
